import { Config } from '../config';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, throwError } from 'rxjs';
import { CommonService } from './common.service';
import { ProvisioningService } from './provisioning.service';
import { ApiResponse } from '../model/provisioning';
import { Dvcr } from '../model/Dvcr';
import { DvcrListItem } from '../model/Dvcr';
import * as moment from 'moment';

import { TranslateService } from '@ngx-translate/core';
import { NavigationNotificationBadgeData } from '../model/user';
import { DateFilter } from './interfaces/DateFilter';

@Injectable({ providedIn: 'root' })
export class FleetReportDvcrService {
  private mappedPost: unknown;
  public readonly showTrailerID: boolean;

  selectedDate: DateFilter = {
    startDate: moment().startOf('day').valueOf(),
    endDate: moment().endOf('day').valueOf(),
  };

  /**
   * Creates a new instance of FleetReportDvcrService
   */
  constructor(
    private http: HttpClient,
    private cService: CommonService,
    private prService: ProvisioningService,
    private translate: TranslateService
  ) {
    this.showTrailerID =
      this.cService.userProfile.companyPermissions?.permDvcrReportShowTrailerID === this.cService.permLevels.TRUE;
  }

  /**
   * Changes the start and end date for the date filter.
   */
  setSelectedDate(date: any) {
    this.selectedDate.startDate = moment(date, Config.momentDateFormatUs).startOf('day').valueOf();
    this.selectedDate.endDate = moment(date, Config.momentDateFormatUs).endOf('day').valueOf();
  }

  /**
   * Performs the request for the trip data from the API.
   */
  getTrips(headers = null): Observable<any> {
    return this.http
      .get(`${Config.apiRoot.uri}dvir/summaries/company/${this.cService.userProfile.fpId}`, {
        params: {
          endTime: `${this.selectedDate.endDate}`,
          startTime: `${this.selectedDate.startDate}`,
          fetchReportDetail: 'TRUE',
        },
        headers,
      })
      .pipe(
        map(({ bodyResponse }: ApiResponse) => {
          return bodyResponse;
        })
      );
  }

  /**
   * Performs the request for the damages data from the API.
   */
  getDamages(headers = null, boundary, id): Observable<any> {
    return this.http
      .get(`${Config.apiRoot.uri}dvir/damages/${boundary}/${id}`, {
        params: {
          endTime: `${this.selectedDate.endDate}`,
          startTime: `${this.selectedDate.startDate}`,
        },
        headers,
      })
      .pipe(
        map(({ data }: any) => {
          return data;
        })
      );
  }

  /**
   * Performs the request for the ZIP file containing the images.
   */
  getZip(zipfilePath): Observable<any> {
    return this.http.get(`${Config.apiRoot.uri}dvir/${zipfilePath}/zip`).pipe(
      map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
        return bodyResponse.imageBinary;
      })
    );
  }

  /**
   * Performs the request for the ZIP file containing the images.
   */
  getImage(detailId, key): Observable<any> {
    return this.http.get(`${Config.apiRoot.uri}dvir/damages/${detailId}/${key}/image`).pipe(
      map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
        return bodyResponse.imageBinary;
      })
    );
  }

  /**
   * Sets the password for activating the user.
   */
  setPassword(password, token) {
    return this.http
      .post(`${Config.apiRoot.uri}users/activate`, {
        password: password.value,
        token: token,
      })
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  /**
   * @param headers any headers to attach to the requests
   */
  _mapTrips(headers = null): Observable<any> {
    return this.getTrips(headers).pipe(
      map((trips: Dvcr[]): Dvcr[] => {
        this.mappedPost = this.cService.arrayToKeyedObject(
          trips.filter((trip) => {
            return trip.report_type_is_post === 1;
          }),
          'pre_id'
        );

        return trips;
      })
    );
  }

  /**
   * Performs the request for the trip session meta data from the API.
   */
  getDamagesMeta(headers = null): Observable<any> {
    return this.http.get(`${Config.apiRoot.uri}dvir/damages/meta`, {
      params: {},
      headers,
    });
  }

  getDvcrDamageDetails(headers = null, boundary, id): Observable<any> {
    return forkJoin({
      //  dvirs: this.getTrips(headers),
      damages: this.getDamages(headers, boundary, id),
      groupsApi: this.prService.mapGroups(headers),
    }).pipe(
      map(({ damages, groupsApi }): Array<any> => {
        var CATEGORY_NAMES;
        var PREDEFINED_COMMENTS_TEXT;

        var CATEGORY_NAMES_DVCR_002;
        var PREDEFINED_COMMENTS_TEXT_DVCR_002;

        this.translate.get('CATEGORY_NAMES').subscribe({
          next: (translation: any) => {
            CATEGORY_NAMES = translation;
          },
        });
        this.translate.get('PREDEFINED_COMMENTS_TEXT').subscribe({
          next: (translation: any) => {
            PREDEFINED_COMMENTS_TEXT = translation;
          },
        });
        this.translate.get('CATEGORY_NAMES_DVCR_002').subscribe({
          next: (translation: any) => {
            CATEGORY_NAMES_DVCR_002 = translation;
          },
        });
        this.translate.get('PREDEFINED_COMMENTS_TEXT_DVCR_002').subscribe({
          next: (translation: any) => {
            PREDEFINED_COMMENTS_TEXT_DVCR_002 = translation;
          },
        });
        const damages_array = [];
        damages.map((dvcr: any): void => {
          dvcr.categories.map((category: any): void => {
            category.firstName = dvcr.firstName;
            category.lastName = dvcr.lastName;
            category.driver_id = dvcr.user_id;
            category.pin = dvcr.pin;
            category.vrmStatus = dvcr.vrmStatus;
            category.dvir_id = dvcr.id;
            category.trailerID = dvcr.categoryTexts['TRAILER-ENTER_ID'] ?? '';
            category.report_type = dvcr.report_type_is_post == 0 ? 'PRE-TRIP DVIR' : 'POST-TRIP DVIR';
            category.report_type = dvcr.report_type_is_post == 2 ? 'AUDIT' : category.report_type;
            category.report_type = dvcr.report_type_is_post == 3 ? 'DVIR' : category.report_type;
            category.vin = dvcr.vin;

            Object.entries(dvcr).forEach(([key, value]) => {
              if (key.includes('location')) {
                Object.assign(category, { [key]: value });
              }
            });

            category.report_date = moment(dvcr.report_date).format('h:mm a');
            category.style =
              category.severity == 1 ? { color: '#df2828' } : category.severity == 2 ? { color: '#f1c410' } : null;
            if (dvcr.version == 'DVCR_002') {
              PREDEFINED_COMMENTS_TEXT = PREDEFINED_COMMENTS_TEXT_DVCR_002;
              CATEGORY_NAMES = CATEGORY_NAMES_DVCR_002;
            }

            category.category_name = CATEGORY_NAMES[category.category_name]
              ? CATEGORY_NAMES[category.category_name]
              : category.category_name;
            category.comments =
              category.predefined_comments === 'OTHER'
                ? category.comment
                : this.extractComments(PREDEFINED_COMMENTS_TEXT, category.predefined_comments) +
                  ' ' +
                  (category.comment ? category.comment : '');
            let imageLinks = this.extractCommentsAndImageLink(
              PREDEFINED_COMMENTS_TEXT,
              category.predefined_comments,
              category.s3key
            );
            category.moreDetails = {
              detailId: category.id,
              s3key: category.s3key,
              comments: category.comments,
              category: category.category_name,
              imageLinks: imageLinks,
            };

            damages_array.push(category);
          });
        });
        return damages_array;
      })
    );
  }

  extractCommentsAndImageLink(hash, predefinedComments: any, bucketKeys: any) {
    let s3Keys = bucketKeys.split(',');
    let commentsArray = predefinedComments.split(',');
    let comments = [];

    commentsArray.forEach((predefinedComment) => {
      let comment = '';
      if (hash[predefinedComment.trim()]) {
        comment = hash[predefinedComment.trim()];
      } else {
        comment = predefinedComment.trim();
      }

      let s3key = s3Keys.find((s) => s.indexOf(predefinedComment.trim()) > -1);
      if (!s3key) {
        s3key = s3Keys.find((s) => s.indexOf(predefinedComment.trim().toLowerCase()) > -1);
      }

      comments.push({ predefinedComment: predefinedComment, comment: comment, s3key: s3key });
    });
    return comments;
  }

  /**
   * Combines various requests together and maps the data into a single object.
   *
   * @param headers Any headers to attach to the requests
   */
  getTripsData(headers = null): Observable<any> {
    let CATEGORY_NAMES;
    let PREDEFINED_COMMENTS_TEXT;
    var CATEGORY_NAMES_DVCR_002;
    var PREDEFINED_COMMENTS_TEXT_DVCR_002;
    return forkJoin({
      trips: this._mapTrips(headers),
      groupsApi: this.prService.mapGroups(headers),
    }).pipe(
      map(({ trips, groupsApi }): Array<DvcrListItem> => {
        const data = [];

        this.translate.get('CATEGORY_NAMES').subscribe({
          next: (translation: any) => {
            CATEGORY_NAMES = translation;
          },
        });
        this.translate.get('PREDEFINED_COMMENTS_TEXT').subscribe({
          next: (translation: any) => {
            PREDEFINED_COMMENTS_TEXT = translation;
          },
        });

        this.translate.get('CATEGORY_NAMES_DVCR_002').subscribe({
          next: (translation: any) => {
            CATEGORY_NAMES_DVCR_002 = translation;
          },
        });
        this.translate.get('PREDEFINED_COMMENTS_TEXT_DVCR_002').subscribe({
          next: (translation: any) => {
            PREDEFINED_COMMENTS_TEXT_DVCR_002 = translation;
          },
        });
        trips.map((trip: Dvcr): void => {
          if (trip.report.version == 'DVCR_002') {
            PREDEFINED_COMMENTS_TEXT = PREDEFINED_COMMENTS_TEXT_DVCR_002;
            CATEGORY_NAMES = CATEGORY_NAMES_DVCR_002;
          }
          if (trip.report_type_is_post != 1) {
            data.push({
              id: trip.id,
              firstName: trip.firstName,
              lastName: trip.lastName,
              vin: trip.vin,
              trailerID: this.showTrailerID ? this.extractTrailerID(trip) : null,
              driverId: trip.user_id,
              station: groupsApi[trip.group_id] ? `${groupsApi[trip.group_id].name}` : '',
              preTrip: moment.unix(trip.report_time / 1000).format('h:mm a'),
              s1Pre: trip.severity1_count,
              s2Pre: trip.severity2_count,
              vrmStatus: trip.vrmStatus,
              same: this.mappedPost[trip.id] ? (this.mappedPost[trip.id].report.sameCondition ? 'Y' : 'N') : '',
              postTrip: this.mappedPost[trip.id]
                ? moment.unix(this.mappedPost[trip.id].report_time / 1000).format('h:mm a')
                : null,
              s1Post: this.mappedPost[trip.id] ? this.mappedPost[trip.id].severity1_count : null,
              s2Post: this.mappedPost[trip.id] ? this.mappedPost[trip.id].severity2_count : null,
              moreDetails: {
                same: this.mappedPost[trip.id] ? (this.mappedPost[trip.id].report.sameCondition ? true : false) : false,
                zipPathPre: trip.zipfilePath,
                zipPathPost: this.mappedPost[trip.id] ? this.mappedPost[trip.id].zipfilePath : '',
                timeStampPre: moment.unix(trip.report_time / 1000).format('h:mm a'),
                timeStampPost: this.mappedPost[trip.id]
                  ? moment.unix(this.mappedPost[trip.id].report_time / 1000).format('h:mm a')
                  : '',

                preDamageLists: trip.report.categories
                  .filter((category) => category.hasDamage && category.name !== 'EXTRA')
                  .map((category) => {
                    const isOther = category.predefinedComments?.endsWith('OTHER');
                    const predefinedComment = this.extractComments(
                      PREDEFINED_COMMENTS_TEXT,
                      category.predefinedComments
                    );

                    return {
                      title: CATEGORY_NAMES[category.name],
                      tit: category.name,
                      textFieldData: category.textFieldData ? this.parseTextFieldData(category.textFieldData) : null,
                      content:
                        category.comment && isOther ? `${predefinedComment}: ${category.comment}` : predefinedComment,
                      style:
                        category.severity == 1
                          ? { color: '#df2828' }
                          : category.severity == 2
                          ? { color: '#f1c410' }
                          : null,
                    };
                  }),
                postDamageLists: this.mappedPost[trip.id]
                  ? this.mappedPost[trip.id].report.categories
                      .filter((category) => category.hasDamage && category.name !== 'EXTRA')
                      .map((category) => {
                        const isOther = category.predefinedComments?.endsWith('OTHER');

                        const predefinedComment = this.extractComments(
                          PREDEFINED_COMMENTS_TEXT,
                          category.predefinedComments
                        );

                        return {
                          title: CATEGORY_NAMES[category.name],
                          tit: category.name,
                          textFieldData: category.textFieldData
                            ? this.parseTextFieldData(category.textFieldData)
                            : null,
                          content:
                            category.comment && isOther
                              ? `${predefinedComment}: ${category.comment}`
                              : predefinedComment,
                          style:
                            category.severity == 1
                              ? { color: '#df2828' }
                              : category.severity == 2
                              ? { color: '#f1c410' }
                              : null,
                        };
                      })
                  : {},
              },
            });
          }
        });

        return data;
      })
    );
  }

  private parseTextFieldData(data: string) {
    const textFieldData = {};
    const json: { [key: string]: string }[] = JSON.parse(data);

    json.forEach((jsonData) =>
      Object.entries(jsonData).forEach(([key, value]) => {
        textFieldData[`PREDEFINED_COMMENTS_TEXT.${key}`] = value;
      })
    );

    return Object.keys(textFieldData).length > 0 ? textFieldData : null;
  }

  extractTrailerID(trip) {
    try {
      let trailer_report = trip.report.categories.find((category) => category.name === 'TRAILER');
      let trailer_data = JSON.parse(trailer_report['textFieldData']);
      let trailer_info = trailer_data[0];

      return trailer_info['TRAILER-ENTER_ID'];
    } catch (error) {
      return;
    }
  }

  extractComments(hash, predefinedComments) {
    let commentsArray = predefinedComments.split(',');
    let comments = '';
    commentsArray.forEach((predefinedComment) => {
      if (comments != '') {
        comments += ', ';
      }
      if (hash[predefinedComment.trim()]) {
        comments += hash[predefinedComment.trim()];
      } else {
        comments += predefinedComment.trim();
      }
    });
    return comments;
  }

  /**
   * Loads the data for the notifications badge.
   */
  loadNotifications(): Observable<any> {
    return this.http
      .get(`${Config.apiRoot.uri}dvir/stat`, {
        params: {
          companyId: `${this.cService.userProfile.fpId}`,
          endTime: `${moment().endOf('day').valueOf()}`,
          startTime: `${moment().startOf('day').valueOf()}`,
        },
      })
      .pipe(map(({ bodyResponse }: ApiResponse) => bodyResponse));
  }

  /**
   * Returns the total amount of notifications
   */
  getNotificationCount(
    dvcrDamageDetailsS1: number = 0,
    dvcrDamageDetailsS2: number = 0
  ): Observable<NavigationNotificationBadgeData> {
    return this.loadNotifications().pipe(
      map((response) => {
        let damageData = null;

        if (response) {
          for (let i = 0; i < response.length; i++) {
            dvcrDamageDetailsS1 += response[i].severity1_count;
            dvcrDamageDetailsS2 += response[i].severity2_count;
          }

          damageData = {
            total: dvcrDamageDetailsS1 + dvcrDamageDetailsS2,
            dvcrDamageDetailsS1,
            dvcrDamageDetailsS2,
          };
        }

        return damageData;
      }),
      catchError((err) => {
        this.cService.showToast(this.translate.instant('DVCR').failedToLoadDvcrNotifications);
        return throwError(err);
      })
    );
  }
}
